import React from "react";
import { Box, CircularProgress, Grid, Paper, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";

const numberFormatter = new Intl.NumberFormat("es-VE");

const kpiItems = [
  {
    key: "total",
    label: "Total registros",
    icon: FormatListNumberedOutlinedIcon,
    color: "info.main",
  },
  { key: "pending", label: "Pendientes", icon: PendingActionsOutlinedIcon, color: "warning.main" },
  { key: "verified", label: "Verificados", icon: CheckCircleOutlineIcon, color: "success.main" },
  { key: "errors", label: "Con errores", icon: ErrorOutlineIcon, color: "error.main" },
];

const displayValue = (value) =>
  value === null || value === undefined ? "Sin definir" : numberFormatter.format(Number(value) || 0);

export const getRevisionProgressData = (summary) => {
  const expected = Number(summary?.target?.expected);
  const transcribed = Number(summary?.progress?.transcribed ?? summary?.progress?.total ?? 0);
  const current = Number.isFinite(transcribed) && transcribed > 0 ? transcribed : 0;
  const hasMeta = Number.isFinite(expected) && expected > 0;
  const rawPercentage = hasMeta ? (current / expected) * 100 : 0;

  return {
    current,
    expected: hasMeta ? expected : 0,
    hasMeta,
    percentage: Math.max(0, rawPercentage),
    displayPercentage: Math.min(100, Math.max(0, Math.round(rawPercentage))),
    visualPercentage: Math.min(100, Math.max(0, rawPercentage)),
  };
};

export const getRevisionProgressColor = ({ percentage, hasMeta }) => {
  if (!hasMeta) return "text.disabled";
  if (percentage < 40) return "error.main";
  if (percentage < 80) return "warning.main";
  if (percentage < 100) return "success.light";
  return "success.main";
};

const RevisionProgressIndicator = ({ summary }) => {
  const progress = getRevisionProgressData(summary);
  const color = getRevisionProgressColor(progress);
  const accessibleLabel = progress.hasMeta
    ? `Avance de transcripción: ${progress.displayPercentage}%`
    : "Avance de transcripción: sin meta";

  return (
    <Stack alignItems="center" spacing={0.25} flexShrink={0}>
      <Box
        role="img"
        aria-label={accessibleLabel}
        sx={{ position: "relative", width: 58, height: 58 }}
      >
        <CircularProgress
          variant="determinate"
          value={100}
          size={58}
          thickness={4.5}
          sx={{ color: "action.disabledBackground", position: "absolute", inset: 0 }}
        />
        <CircularProgress
          variant="determinate"
          value={progress.visualPercentage}
          size={58}
          thickness={4.5}
          sx={{ color, position: "absolute", inset: 0 }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="caption" fontWeight={900} color={progress.hasMeta ? "text.primary" : "text.secondary"}>
            {progress.displayPercentage}%
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
        {progress.hasMeta
          ? `${numberFormatter.format(progress.current)} / ${numberFormatter.format(progress.expected)}`
          : "Sin meta"}
      </Typography>
    </Stack>
  );
};

const RevisionKpis = ({ summary }) => {
  const values = {
    total: summary?.progress?.total ?? summary?.progress?.transcribed,
    verified: summary?.progress?.verified,
    pending: summary?.progress?.pending,
    errors: summary?.progress?.errors,
  };

  return (
    <Grid container spacing={1.5}>
      {kpiItems.map(({ key, label, icon: Icon, color }) => (
        <Grid item xs={key === "total" ? 12 : 6} sm={6} md={3} key={key}>
          <Paper
            variant="outlined"
            sx={{ height: "100%", p: { xs: 1.25, md: 1.5 }, borderRadius: 1, bgcolor: "background.paper" }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                <Icon sx={{ color, fontSize: 30, flexShrink: 0 }} aria-hidden="true" />
                <Box minWidth={0}>
                  <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.1 }}>
                    {displayValue(values[key])}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    {label}
                  </Typography>
                </Box>
              </Stack>
              {key === "total" && <RevisionProgressIndicator summary={summary} />}
            </Stack>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

export default RevisionKpis;
